import { useState } from 'react';
import type { CloudPCStatus } from '../types';

interface SearchFiltersProps {
  onSearch: (filters: SearchFilters) => void;
}

export interface SearchFilters {
  searchType: 'id' | 'userId' | 'phone';
  searchValue: string;
  status: CloudPCStatus | 'all';
  productName: string[];
  os: string[];
  datacenter: string;
  createTimeStart: string;
  createTimeEnd: string;
  expireTimeStart: string;
  expireTimeEnd: string;
  isOverdue: 'all' | 'yes' | 'no';
}

export default function SearchFilters({ onSearch }: SearchFiltersProps) {
  const [searchType, setSearchType] = useState<'id' | 'userId' | 'phone'>('id');
  const [searchValue, setSearchValue] = useState('');
  const [status, setStatus] = useState<CloudPCStatus | 'all'>('all');
  const [productName, setProductName] = useState<string[]>([]);
  const [os, setOs] = useState<string[]>([]);
  const [datacenter, setDatacenter] = useState('');
  const [createTimeStart, setCreateTimeStart] = useState('');
  const [createTimeEnd, setCreateTimeEnd] = useState('');
  const [expireTimeStart, setExpireTimeStart] = useState('');
  const [expireTimeEnd, setExpireTimeEnd] = useState('');
  const [isOverdue, setIsOverdue] = useState<'all' | 'yes' | 'no'>('all');
  const [showFilters, setShowFilters] = useState(false);

  const handleSearch = () => {
    onSearch({
      searchType,
      searchValue,
      status,
      productName,
      os,
      datacenter,
      createTimeStart,
      createTimeEnd,
      expireTimeStart,
      expireTimeEnd,
      isOverdue,
    });
  };

  const handleReset = () => {
    setSearchType('id');
    setSearchValue('');
    setStatus('all');
    setProductName([]);
    setOs([]);
    setDatacenter('');
    setCreateTimeStart('');
    setCreateTimeEnd('');
    setExpireTimeStart('');
    setExpireTimeEnd('');
    setIsOverdue('all');
    onSearch({
      searchType: 'id',
      searchValue: '',
      status: 'all',
      productName: [],
      os: [],
      datacenter: '',
      createTimeStart: '',
      createTimeEnd: '',
      expireTimeStart: '',
      expireTimeEnd: '',
      isOverdue: 'all',
    });
  };

  const productOptions = ['标准云电脑', '高性能云电脑', 'GPU服务器', 'PC Farm'];
  const osOptions = ['Windows 10', 'Windows 11', 'Ubuntu 20.04', 'Ubuntu 22.04', 'CentOS 7'];
  const datacenterOptions = ['北京数据中心', '上海数据中心', '深圳数据中心', '广州数据中心'];

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 mb-4">
      <div className="flex items-center gap-4 mb-4">
        <select
          value={searchType}
          onChange={(e) => setSearchType(e.target.value as 'id' | 'userId' | 'phone')}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="id">云电脑ID</option>
          <option value="userId">用户ID</option>
          <option value="phone">用户手机号</option>
        </select>
        <input
          type="text"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="请输入搜索内容"
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={handleSearch}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors whitespace-nowrap"
        >
          <i className="ri-search-line mr-2"></i>
          搜索
        </button>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors whitespace-nowrap"
        >
          <i className={`ri-filter-${showFilters ? 'fill' : 'line'} mr-2`}></i>
          筛选
        </button>
        <button
          onClick={handleReset}
          className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-2"></i>
          重置
        </button>
      </div>

      {showFilters && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              云电脑状态
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as CloudPCStatus | 'all')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">全部</option>
              <option value="running">运行中</option>
              <option value="stopped">已停止</option>
              <option value="creating">创建中</option>
              <option value="fault">故障</option>
              <option value="expired">已过期</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              操作系统
            </label>
            <select
              multiple
              value={os}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                setOs(selected);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              size={3}
            >
              {osOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              所属机房
            </label>
            <select
              value={datacenter}
              onChange={(e) => setDatacenter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">全部</option>
              {datacenterOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              创建时间
            </label>
            <div className="flex items-center gap-2">
              <input
                type="datetime-local"
                value={createTimeStart}
                onChange={(e) => setCreateTimeStart(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-gray-500">至</span>
              <input
                type="datetime-local"
                value={createTimeEnd}
                onChange={(e) => setCreateTimeEnd(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              到期时间
            </label>
            <div className="flex items-center gap-2">
              <input
                type="datetime-local"
                value={expireTimeStart}
                onChange={(e) => setExpireTimeStart(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-gray-500">至</span>
              <input
                type="datetime-local"
                value={expireTimeEnd}
                onChange={(e) => setExpireTimeEnd(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              是否欠费
            </label>
            <select
              value={isOverdue}
              onChange={(e) => setIsOverdue(e.target.value as 'all' | 'yes' | 'no')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">全部</option>
              <option value="yes">是</option>
              <option value="no">否</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
